// features/projects/templates/continuum/donor-brief.js
// ═══════════════════════════════════════════════════════════
// CONTINUUM DONOR BRIEF — Tab 5
//
// First application view (Phase 2 of the build plan). Translates
// research findings into something a conservation donor or policy
// stakeholder would actually consult before allocating resources.
//
// Three sections:
//   1. Findings Summary — the 3 strongest comparisons from the
//      latest simulation run, in plain English
//   2. Watch List — top species/countries by Tipping Signature
//      score (the most "near-tipping" systems detected by CSD)
//   3. Intervention Options — existing interventions ranked by
//      ROI proxy (low cost, high-urgency target, evidence-backed)
//
// Honest about limits: cost figures are AI-generated estimates,
// not validated quotes. Outcomes are projected, not measured.
// Provenance shown for every number.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { C, FONT as F } from './tokens';
import { DYNAMICS } from '../../../../lib/continuum/simulation/methodology';

const DYN_BY_KEY = Object.fromEntries(DYNAMICS.map(d => [d.key, d]));

export default function ContinuumDonorBrief({ workspaceId }) {
  const [run, setRun] = useState(null);
  const [comparisons, setComparisons] = useState(null);
  const [interventions, setInterventions] = useState(null);

  // Per-row "generating" state for the watch list — keyed by event identity
  const [generatingKey, setGeneratingKey] = useState(null);
  const [generationError, setGenerationError] = useState(null);

  const reload = useCallback(async () => {
    if (!workspaceId) return;
    const [compRes, intRes] = await Promise.all([
      fetch(`/api/continuum/comparisons?workspace_id=${workspaceId}`),
      fetch(`/api/continuum/interventions?workspace_id=${workspaceId}`),
    ]);
    if (compRes.ok) {
      const j = await compRes.json();
      setRun(j.run);
      setComparisons(j.comparisons || []);
    } else {
      setComparisons([]);
    }
    if (intRes.ok) {
      const j = await intRes.json();
      setInterventions(j.interventions || []);
    } else {
      setInterventions([]);
    }
  }, [workspaceId]);

  useEffect(() => { reload(); }, [reload]);

  // Delete an intervention. Native confirm — this is a donor brief,
  // an accidental delete is a cheap mistake but worth a beat to
  // think about. After delete, the watch list will show + Intervention
  // again for that species so re-generation is two clicks total.
  const deleteIntervention = useCallback(async (intervention) => {
    if (!intervention?.id) return;
    const targetLabel = intervention.trigger_common || intervention.trigger_binomial || intervention.title || 'this intervention';
    const ok = typeof window !== 'undefined' && window.confirm(
      `Delete the intervention for ${targetLabel}?\n\nYou can re-generate it from the watch list afterwards.`
    );
    if (!ok) return;
    try {
      const res = await fetch(`/api/continuum/interventions?id=${intervention.id}`, { method: 'DELETE' });
      if (res.ok) {
        await reload();
      } else {
        const j = await res.json().catch(() => ({}));
        setGenerationError(`Delete failed: ${j.error || `Status ${res.status}`}`);
      }
    } catch (err) {
      setGenerationError(`Delete failed: ${err.message}`);
    }
  }, [reload]);

  // Generate a global-context intervention for a watch-list entry.
  // Uses the system_name as the trigger_binomial (the global path
  // matches by binomial OR system_name, so either form works).
  const generateForEvent = useCallback(async (event) => {
    if (!workspaceId || !event?.system_name) return;
    const key = event.system_name;
    setGeneratingKey(key);
    setGenerationError(null);
    try {
      const res = await fetch('/api/continuum/propose-intervention', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          workspace_id: workspaceId,
          trigger_binomial: extractBinomialFromSystemName(event.system_name),
          context_hint: 'global_pattern',
        }),
      });
      const j = await res.json();
      if (!res.ok) {
        setGenerationError(`${key}: ${j.error || `Status ${res.status}`}`);
      } else {
        // Refresh interventions list
        await reload();
      }
    } catch (err) {
      setGenerationError(`${key}: ${err.message}`);
    }
    setGeneratingKey(null);
  }, [workspaceId, reload]);

  const loading = comparisons === null || interventions === null;

  return (
    <div style={{
      height:'100%', background:C.bg, color:C.text,
      fontFamily:F.mono, fontSize:12,
      display:'flex', flexDirection:'column', overflow:'hidden',
    }}>
      <Header run={run} loading={loading} onReload={reload} />

      <div style={{ flex:1, minHeight:0, overflow:'auto', padding:'18px 22px' }}>
        {loading ? (
          <Empty msg="Loading donor brief…" />
        ) : !run ? (
          <Empty msg="No simulation run yet. Run a simulation to generate the donor brief." />
        ) : (
          <>
            <FindingsSection comparisons={comparisons} />
            {generationError && (
              <div style={{
                padding:'10px 14px', marginBottom:14,
                background:'rgba(196,80,80,.06)', border:'1px solid rgba(196,80,80,.3)',
                borderRadius:6, color:C.terra, fontSize:11,
              }}>
                Intervention generation failed: {generationError}
              </div>
            )}
            <WatchListSection
              comparisons={comparisons}
              interventions={interventions}
              onGenerate={generateForEvent}
              generatingKey={generatingKey}
            />
            <InterventionsSection
              interventions={interventions}
              comparisons={comparisons}
              onDelete={deleteIntervention}
            />
            <Provenance run={run} comparisons={comparisons} interventions={interventions} />
          </>
        )}
      </div>
    </div>
  );
}

// ─── Header strip ──────────────────────────────────────────
function Header({ run, loading, onReload }) {
  return (
    <div style={{
      display:'flex', alignItems:'center',
      padding:'0 20px', height:42,
      borderBottom:`1px solid ${C.b1}`,
      background:C.s1, flexShrink:0, gap:14,
    }}>
      <div style={{
        display:'flex', alignItems:'center', gap:6,
        fontSize:9, padding:'3px 10px', borderRadius:4,
        background:'rgba(184,149,106,.08)',
        border:'1px solid rgba(184,149,106,.20)',
        color:C.amber,
      }}>
        <div style={{ width:5, height:5, borderRadius:'50%', background:C.amber }} />
        Donor Decision Brief
      </div>
      {run && (
        <div style={{ fontSize:9, color:C.dim }}>
          Based on Run {run.id?.slice(0,6)} · {run.completed_at ? new Date(run.completed_at).toLocaleDateString(undefined, { month:'short', day:'numeric', year:'numeric' }) : ''}
        </div>
      )}
      <div style={{ marginLeft:'auto' }}>
        <button
          onClick={onReload}
          disabled={loading}
          style={{
            fontSize:9, padding:'5px 14px', borderRadius:4,
            background:'transparent', border:`1px solid ${C.b2}`,
            color:C.static, cursor: loading ? 'not-allowed' : 'pointer',
            fontFamily:F.mono, letterSpacing:'0.06em',
          }}
        >
          {loading ? '⟳ Loading' : '↻ Refresh'}
        </button>
      </div>
    </div>
  );
}

function Empty({ msg }) {
  return (
    <div style={{ height:'60vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ color:C.dim, fontSize:12, fontStyle:'italic' }}>{msg}</div>
    </div>
  );
}

// ─── Section 1 — Findings Summary ─────────────────────────
// Pull the 3 strongest comparisons (most defined verdicts +
// highest confidence) and render them as a row of cards.
function FindingsSection({ comparisons }) {
  const ranked = (comparisons || [])
    .filter(c => c.verdict && !['pending', 'insufficient_data', 'error'].includes(c.verdict))
    .sort((a, b) => verdictPriority(b) - verdictPriority(a))
    .slice(0, 3);

  if (ranked.length === 0) {
    return (
      <SectionShell title="Findings" subtitle="No defensible findings yet from this run">
        <div style={{ fontSize:11, color:C.dim, fontStyle:'italic', padding:14 }}>
          Findings will appear here once dynamics produce verdicts. Run a simulation with at least one wired dynamic.
        </div>
      </SectionShell>
    );
  }

  return (
    <SectionShell title="Findings" subtitle="Strongest comparisons from the latest run">
      <div style={{ display:'grid', gridTemplateColumns:`repeat(${ranked.length}, 1fr)`, gap:12 }}>
        {ranked.map(c => <FindingMini key={c.id} c={c} />)}
      </div>
    </SectionShell>
  );
}

function verdictPriority(c) {
  const v = c.verdict;
  const conf = c.confidence;
  let s = 0;
  if      (v === 'DIVERGENT')     s += 3;
  else if (v === 'NEAR-PARALLEL') s += 2;
  else if (v === 'PARALLEL')      s += 1;
  if      (conf === 'HIGH')   s += 3;
  else if (conf === 'MEDIUM') s += 2;
  else if (conf === 'LOW')    s += 1;
  return s;
}

function FindingMini({ c }) {
  const dyn = DYN_BY_KEY[c.dynamic_key] || {};
  const verdict = c.verdict;
  const verdictColor = verdict === 'DIVERGENT' ? C.terra
    : verdict === 'NEAR-PARALLEL' ? C.gold
    : verdict === 'PARALLEL' ? C.sage
    : C.dim;
  return (
    <div style={{
      background:C.s1, border:`1px solid ${C.b2}`, borderRadius:6, padding:12,
    }}>
      <div style={{ fontFamily:F.mono, fontSize:8, letterSpacing:'0.16em', textTransform:'uppercase', color:C.dim, marginBottom:6 }}>
        {dyn.short || c.dynamic_key}
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
        <div style={{
          fontSize:9, padding:'2px 7px', borderRadius:3,
          background:`${verdictColor}15`, border:`1px solid ${verdictColor}50`,
          color:verdictColor, letterSpacing:'0.06em',
        }}>
          {verdict}
        </div>
        <div style={{ fontSize:9, color:C.dim }}>{c.confidence || '—'}</div>
      </div>
      <div style={{ fontSize:11, color:C.mid, lineHeight:1.55 }}>
        {(c.finding_text || '').slice(0, 240)}{(c.finding_text || '').length > 240 ? '…' : ''}
      </div>
    </div>
  );
}

// ─── Section 2 — Watch List ───────────────────────────────
// Top species/countries by Tipping Signature score. These are
// systems that show CSD signatures — early warnings that they
// may be approaching a tipping point.
function WatchListSection({ comparisons, interventions, onGenerate, generatingKey }) {
  const tipping = (comparisons || []).find(c => c.dynamic_key === 'tipping_signature');
  if (!tipping || !tipping.data_inputs) {
    return (
      <SectionShell title="Watch List" subtitle="Systems showing tipping-point early-warning signatures">
        <div style={{ fontSize:11, color:C.dim, fontStyle:'italic', padding:14 }}>
          Tipping Signature dynamic hasn&apos;t produced events yet for this run.
        </div>
      </SectionShell>
    );
  }

  const ecoEvents   = (tipping.data_inputs.eco?.events   || []).map(e => ({ ...e, _side: 'eco' }));
  const humanEvents = (tipping.data_inputs.human?.events || []).map(e => ({ ...e, _side: 'human' }));
  const all = [...ecoEvents, ...humanEvents].sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  const top = all.slice(0, 15);

  // Index existing interventions by trigger common/binomial so we can
  // mark watch-list rows that already have an intervention drafted.
  const haveIntervention = new Set();
  for (const i of (interventions || [])) {
    if (i.trigger_common)   haveIntervention.add(i.trigger_common.toLowerCase());
    if (i.trigger_binomial) haveIntervention.add(i.trigger_binomial.toLowerCase());
  }

  return (
    <SectionShell title="Watch List" subtitle="Top 15 systems by Tipping Signature score (CSD-based early warning)">
      <div style={{
        background:C.s1, border:`1px solid ${C.b1}`, borderRadius:6, overflow:'hidden',
      }}>
        <div style={{
          display:'grid', gridTemplateColumns:'40px 2fr 70px 60px 70px 50px 130px',
          padding:'8px 14px', borderBottom:`1px solid ${C.b1}`,
          fontFamily:F.mono, fontSize:8, letterSpacing:'0.12em',
          textTransform:'uppercase', color:C.dim, gap:6,
        }}>
          <div>Rank</div>
          <div>System</div>
          <div>Domain</div>
          <div>Score</div>
          <div>AC1</div>
          <div style={{ textAlign:'right' }}>n pts</div>
          <div style={{ textAlign:'right' }}>Action</div>
        </div>
        {top.map((e, i) => {
          const sysLower = (e.system_name || '').toLowerCase();
          const hasInt = haveIntervention.has(sysLower) ||
            [...haveIntervention].some(name => name && (sysLower.includes(name) || name.includes(sysLower.split('—')[0].trim())));
          const generating = generatingKey === e.system_name;
          return (
            <div key={i} style={{
              display:'grid', gridTemplateColumns:'40px 2fr 70px 60px 70px 50px 130px',
              padding:'7px 14px', gap:6,
              borderBottom: i < top.length - 1 ? `1px solid ${C.b3 || C.b2}` : 'none',
              alignItems:'center', fontSize:11,
            }}>
              <div style={{ fontFamily:F.mono, color:C.dim }}>{String(i + 1).padStart(2, '0')}</div>
              <div style={{ color:C.text, fontFamily:F.sans, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {e.system_name || '(unnamed)'}
              </div>
              <div style={{
                fontSize:9, fontFamily:F.mono, padding:'2px 7px', borderRadius:3, display:'inline-block', width:'fit-content',
                background: e._side === 'eco' ? 'rgba(122,158,130,.12)' : 'rgba(196,80,80,.12)',
                color: e._side === 'eco' ? C.sage : C.terra,
                letterSpacing:'0.06em',
              }}>
                {e._side}
              </div>
              <div style={{ fontFamily:F.mono, color: scoreColor(e.value), fontWeight: 500 }}>
                {e.value != null ? Number(e.value).toFixed(3) : '—'}
              </div>
              <div style={{ fontFamily:F.mono, color:C.mid, fontSize:10 }}>
                {e.lag1_autocorrelation != null ? Number(e.lag1_autocorrelation).toFixed(3) : '—'}
              </div>
              <div style={{ fontFamily:F.mono, color:C.dim, fontSize:10, textAlign:'right' }}>
                {e.n_points ?? '—'}
              </div>
              <div style={{ textAlign:'right' }}>
                {hasInt ? (
                  <span style={{
                    fontSize:8, fontFamily:F.mono, color:C.sage, letterSpacing:'0.1em',
                    padding:'2px 7px', borderRadius:3,
                    background:'rgba(122,158,130,.10)', border:'1px solid rgba(122,158,130,.30)',
                  }}>✓ DRAFTED</span>
                ) : (
                  <button
                    onClick={() => onGenerate?.(e)}
                    disabled={generating || !onGenerate}
                    style={{
                      fontSize:8, fontFamily:F.mono, letterSpacing:'0.08em',
                      padding:'4px 9px', borderRadius:3, cursor: generating ? 'not-allowed' : 'pointer',
                      background: generating ? 'rgba(155,143,196,.10)' : 'rgba(184,149,106,.10)',
                      border: `1px solid ${generating ? 'rgba(155,143,196,.30)' : 'rgba(184,149,106,.30)'}`,
                      color: generating ? C.purple : C.amber,
                      textTransform:'uppercase',
                    }}
                  >
                    {generating ? '⟳ Drafting' : '+ Intervention'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{
        marginTop:8, fontSize:10, color:C.dim, lineHeight:1.5, fontStyle:'italic',
      }}>
        Higher score = more signs of approaching a tipping point. Score is a composite
        of lag-1 autocorrelation (50%), variance trend (30%), and coefficient of variation (20%).
        Score &gt; 0.6 warrants investigation; &gt; 0.8 is critical. Click <strong>+ Intervention</strong>
        to draft a global-pattern intervention for any watch-list system.
      </div>
    </SectionShell>
  );
}

// Best-effort extraction of a binomial-like identifier from a
// system_name. LPI gives "Northern goshawk (Accipiter gentilis)" —
// we want "Accipiter gentilis". OWID gives "Liberia — GDP per capita"
// — there's no binomial; the global path matches by system_name too,
// so we pass the parenthetical if present, else the full name.
function extractBinomialFromSystemName(systemName) {
  if (!systemName) return '';
  const m = systemName.match(/\(([^)]+)\)/);
  if (m) return m[1].trim();
  return systemName.trim();
}

function scoreColor(v) {
  if (v == null) return C.dim;
  if (v >= 0.8) return C.terra;
  if (v >= 0.6) return C.amber;
  if (v >= 0.4) return C.gold;
  return C.sage;
}

// ─── Section 3 — Interventions ────────────────────────────
function InterventionsSection({ interventions, comparisons, onDelete }) {
  const list = (interventions || []).filter(i => i.status !== 'dismissed');
  if (list.length === 0) {
    return (
      <SectionShell title="Intervention Options" subtitle="Conservation actions ranked for donor consideration">
        <div style={{ fontSize:11, color:C.dim, fontStyle:'italic', padding:14 }}>
          No interventions proposed yet. Click <strong style={{ color:C.amber }}>+ Intervention</strong> next to a watch-list system above to draft one, or use the Dashboard tab&apos;s intervention generator for Florida indicator species.
        </div>
      </SectionShell>
    );
  }

  // Build a watch-list lookup so interventions targeting watch-list
  // systems get an "URGENT" flag.
  const watchSystems = new Set();
  const tipping = (comparisons || []).find(c => c.dynamic_key === 'tipping_signature');
  if (tipping?.data_inputs) {
    const ecoEvents   = tipping.data_inputs.eco?.events   || [];
    const humanEvents = tipping.data_inputs.human?.events || [];
    for (const e of [...ecoEvents, ...humanEvents]) {
      if (e.value != null && e.value >= 0.6 && e.system_name) {
        watchSystems.add(e.system_name.toLowerCase());
      }
    }
  }

  const enriched = list.map(i => ({
    ...i,
    _isUrgent: targetsWatchSystem(i, watchSystems),
    _costRank: i.cost_amount != null ? i.cost_amount : Infinity,
    _ctx: i.context_type || 'florida_indicator',
  }));

  // Split into two groups by context — honest research scope reflected in UI
  const florida = enriched.filter(i => i._ctx === 'florida_indicator')
    .sort((a, b) => {
      if (a._isUrgent && !b._isUrgent) return -1;
      if (!a._isUrgent && b._isUrgent) return 1;
      return (a._costRank || 0) - (b._costRank || 0);
    });
  const global = enriched.filter(i => i._ctx === 'global_pattern')
    .sort((a, b) => {
      if (a._isUrgent && !b._isUrgent) return -1;
      if (!a._isUrgent && b._isUrgent) return 1;
      return (a._costRank || 0) - (b._costRank || 0);
    });
  const otherCtx = enriched.filter(i => i._ctx !== 'florida_indicator' && i._ctx !== 'global_pattern');

  return (
    <SectionShell title="Intervention Options" subtitle={`${enriched.length} actions across two contexts`}>
      {global.length > 0 && (
        <ContextGroup
          label="Global Pattern — trajectory-based, validate locally"
          tag="GLOBAL"
          tagColor={C.purple || C.amber}
          subtitle="Drafted from population trajectory + analog cases. Reference IUCN action plans before commitment."
          items={global.slice(0, 12)}
          totalCount={global.length}
          onDelete={onDelete}
        />
      )}
      {florida.length > 0 && (
        <ContextGroup
          label="Florida Everglades — deep local context"
          tag="FLORIDA"
          tagColor={C.amber}
          subtitle="Drafted from indicator-species cascade + local ecology + USFWS/SFWMD source templates."
          items={florida.slice(0, 12)}
          totalCount={florida.length}
          onDelete={onDelete}
        />
      )}
      {otherCtx.length > 0 && (
        <ContextGroup
          label="Other"
          tag="OTHER"
          tagColor={C.dim}
          subtitle="User-created or legacy interventions"
          items={otherCtx.slice(0, 12)}
          totalCount={otherCtx.length}
          onDelete={onDelete}
        />
      )}
    </SectionShell>
  );
}

function ContextGroup({ label, tag, tagColor, subtitle, items, totalCount, onDelete }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
        <div style={{
          fontSize:8, padding:'2px 8px', borderRadius:3,
          background:`${tagColor}15`, border:`1px solid ${tagColor}50`,
          color:tagColor, letterSpacing:'0.1em', fontFamily:F.mono, fontWeight:500,
        }}>
          {tag} · {totalCount}
        </div>
        <div style={{ fontFamily:F.serif, fontSize:14, color:C.text, fontWeight:300 }}>
          {label}
        </div>
      </div>
      {subtitle && (
        <div style={{ fontSize:10, color:C.dim, fontStyle:'italic', marginBottom:8, lineHeight:1.5 }}>
          {subtitle}
        </div>
      )}
      <div style={{
        display:'grid',
        // 2-up at wide screens, auto-collapses to 1 on narrow.
        // 560px min keeps each card readable; single-card groups
        // still expand to fill the row.
        gridTemplateColumns:'repeat(auto-fit, minmax(560px, 1fr))',
        gap:12,
      }}>
        {items.map(i => <InterventionCard key={i.id} i={i} onDelete={onDelete} />)}
      </div>
      {totalCount > items.length && (
        <div style={{ marginTop:8, fontSize:10, color:C.dim, fontStyle:'italic' }}>
          {totalCount - items.length} more in the Dashboard tab&apos;s intervention library.
        </div>
      )}
    </div>
  );
}

function targetsWatchSystem(intervention, watchSet) {
  if (watchSet.size === 0) return false;
  const candidates = [
    intervention.target_species,
    intervention.target_binomial,
    intervention.target_common,
    intervention.title,
  ].filter(Boolean).map(s => s.toLowerCase());
  for (const c of candidates) {
    for (const w of watchSet) {
      if (c.includes(w) || w.includes(c)) return true;
    }
  }
  return false;
}

function InterventionCard({ i, onDelete }) {
  const cost = i.cost_display || (i.cost_amount != null ? `$${Number(i.cost_amount).toLocaleString()}` : '—');
  const outcomes = Array.isArray(i.outcomes) ? i.outcomes : [];
  const ctx = i.context_type || 'florida_indicator';

  // Per-context accent color anchors the card's identity
  const accent = ctx === 'global_pattern' ? (C.purple || C.amber)
    : ctx === 'manual'       ? C.dim
    :                          C.amber;
  const accentTint = `${accent}10`;
  const accentLine = `${accent}55`;

  // The validation_note from global_pattern lives at the end of
  // data_sources (we appended it server-side). Split it back out
  // so we can render it as its own caution block.
  const { sourcesClean, validationNote } = splitValidation(i.data_sources || '');

  // Target species/system display
  const targetName = i.trigger_common || i.trigger_binomial || '—';
  const targetSci  = i.trigger_binomial && i.trigger_binomial !== i.trigger_common ? i.trigger_binomial : null;

  return (
    <div style={{
      background: i._isUrgent ? 'rgba(196,80,80,.05)' : C.s1,
      border: `1px solid ${i._isUrgent ? 'rgba(196,80,80,.35)' : C.b2}`,
      borderRadius: 8, overflow: 'hidden',
      boxShadow: i._isUrgent ? '0 0 0 1px rgba(196,80,80,.15)' : 'none',
    }}>
      {/* ── ACCENT STRIPE ───────────────────────────────────── */}
      <div style={{ height: 3, background: i._isUrgent ? C.terra : accent }} />

      {/* ── HEADER STRIP ────────────────────────────────────── */}
      <div style={{
        display:'flex', alignItems:'center', gap:10,
        padding:'10px 18px',
        background: i._isUrgent ? 'rgba(196,80,80,.06)' : accentTint,
        borderBottom:`1px solid ${C.b1}`,
      }}>
        <div style={{
          fontSize:8, padding:'3px 9px', borderRadius:3,
          background: i._isUrgent ? 'rgba(196,80,80,.15)' : `${accent}20`,
          border:`1px solid ${i._isUrgent ? 'rgba(196,80,80,.4)' : accentLine}`,
          color: i._isUrgent ? C.terra : accent,
          letterSpacing:'0.14em', fontFamily:F.mono, fontWeight:500,
        }}>
          {ctx === 'global_pattern' ? 'GLOBAL PATTERN'
            : ctx === 'manual'       ? 'MANUAL'
            :                          'FLORIDA INDICATOR'}
        </div>
        {i._isUrgent && (
          <div style={{
            fontSize:8, padding:'3px 9px', borderRadius:3,
            background:'rgba(196,80,80,.18)', border:'1px solid rgba(196,80,80,.5)',
            color:C.terra, letterSpacing:'0.16em', fontFamily:F.mono, fontWeight:500,
          }}>
            ⚠ URGENT — WATCH LIST TARGET
          </div>
        )}
        <div style={{ marginLeft:'auto', fontFamily:F.mono, fontSize:9, color:C.dim, letterSpacing:'0.06em' }}>
          {i.label || ''}
        </div>
        {onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(i); }}
            title="Delete this intervention (you can re-generate from the watch list)"
            style={{
              marginLeft:8, padding:'3px 8px', borderRadius:3,
              background:'transparent', border:`1px solid ${C.b2}`,
              color:C.dim, fontFamily:F.mono, fontSize:9,
              letterSpacing:'0.08em', cursor:'pointer',
              transition:'color 0.12s, border-color 0.12s, background 0.12s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = C.terra;
              e.currentTarget.style.borderColor = C.terra;
              e.currentTarget.style.background = 'rgba(196,80,80,.06)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = C.dim;
              e.currentTarget.style.borderColor = C.b2;
              e.currentTarget.style.background = 'transparent';
            }}
          >
            ✕ DELETE
          </button>
        )}
      </div>

      {/* ── HERO — Title ───────────────────────────────────── */}
      <div style={{ padding:'14px 20px 4px' }}>
        <div style={{
          fontFamily:F.serif, fontSize:18, fontWeight:300, color:C.text,
          lineHeight:1.35, letterSpacing:'-0.005em',
        }}>
          {i.title || '(untitled intervention)'}
        </div>
      </div>

      {/* ── HERO — Stats row (Target / Cost / Region) ─────── */}
      <div style={{
        display:'grid', gridTemplateColumns:'1.4fr 0.9fr 0.9fr',
        gap:14, padding:'10px 20px 14px',
        borderBottom:`1px solid ${C.b1}`,
        alignItems:'start',
      }}>
        <StatCell label="Target">
          <div style={{
            fontFamily:F.serif, fontSize:14, color:C.sage, fontWeight:400,
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
          }}>
            {targetName}
          </div>
          {targetSci && (
            <div style={{
              fontFamily:F.serif, fontSize:10, color:C.dim, fontStyle:'italic', marginTop:2,
              overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
            }}>
              {targetSci}
            </div>
          )}
          {i.target_iucn_status && (
            <div style={{
              display:'inline-block', marginTop:5,
              fontSize:8, padding:'2px 7px', borderRadius:3,
              background:'rgba(196,80,80,.10)', border:'1px solid rgba(196,80,80,.30)',
              color:C.terra, letterSpacing:'0.08em', fontFamily:F.mono,
            }}>
              IUCN {i.target_iucn_status}
            </div>
          )}
        </StatCell>
        <StatCell label="Cost">
          <div style={{
            fontFamily:F.mono, fontSize:20, color:C.gold, fontWeight:500,
            letterSpacing:'-0.01em',
          }}>
            {cost}
          </div>
          {i.cost_description && (
            <div
              title={i.cost_description}
              style={{
                fontSize:9, color:C.dim, marginTop:3, lineHeight:1.4, fontStyle:'italic',
                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
              }}
            >
              {i.cost_description}
            </div>
          )}
        </StatCell>
        <StatCell label="Region">
          <div style={{
            fontFamily:F.serif, fontSize:13, color:C.teal, fontWeight:400,
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
          }}>
            {i.target_region || i.ecosystem || '—'}
          </div>
          {i.target_data_source && (
            <div style={{ fontFamily:F.mono, fontSize:9, color:C.dim, marginTop:4, letterSpacing:'0.06em' }}>
              data: {i.target_data_source}
            </div>
          )}
        </StatCell>
      </div>

      {/* ── PROJECTED OUTCOMES — the meat ────────────────── */}
      {outcomes.length > 0 && (
        <div style={{ padding:'12px 20px' }}>
          <div style={{
            fontFamily:F.mono, fontSize:9, letterSpacing:'0.18em',
            textTransform:'uppercase', color:C.sage, marginBottom:9, fontWeight:500,
          }}>
            ◆ Projected Outcomes
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
            {outcomes.map((o, idx) => (
              <div key={idx} style={{
                display:'flex', alignItems:'flex-start', gap:9,
                padding:'8px 11px',
                background:C.bg, border:`1px solid ${C.b1}`, borderRadius:5,
              }}>
                <div style={{
                  fontFamily:F.mono, fontSize:9, color:C.sage, fontWeight:500,
                  letterSpacing:'0.06em', flexShrink:0, marginTop:2, minWidth:20,
                }}>
                  {String(idx + 1).padStart(2, '0')}
                </div>
                <div style={{ fontSize:11.5, color:C.text, lineHeight:1.6, flex:1 }}>
                  {typeof o === 'string' ? o : (o.label || JSON.stringify(o))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── WINDOW — when does this need to happen? ───────── */}
      {i.window_text && (
        <div style={{ padding:'0 20px 12px' }}>
          <div style={{
            display:'flex', alignItems:'flex-start', gap:9,
            padding:'9px 12px',
            background:'rgba(184,149,106,.06)', border:'1px solid rgba(184,149,106,.25)',
            borderRadius:5,
          }}>
            <div style={{
              fontFamily:F.mono, fontSize:8, letterSpacing:'0.16em', textTransform:'uppercase',
              color:C.amber, fontWeight:500, flexShrink:0, minWidth:60,
            }}>
              ⏱ WINDOW
            </div>
            <div style={{ fontSize:11, color:C.mid, lineHeight:1.55, flex:1 }}>
              {i.window_text}
            </div>
          </div>
        </div>
      )}

      {/* ── VALIDATION NOTE — for global_pattern only ─────── */}
      {validationNote && (
        <div style={{ padding:'0 20px 12px' }}>
          <div style={{
            display:'flex', alignItems:'flex-start', gap:9,
            padding:'9px 12px',
            background:'rgba(155,143,196,.06)', border:'1px solid rgba(155,143,196,.30)',
            borderRadius:5,
          }}>
            <div style={{
              fontFamily:F.mono, fontSize:8, letterSpacing:'0.16em', textTransform:'uppercase',
              color:C.purple || C.amber, fontWeight:500, flexShrink:0, minWidth:70,
            }}>
              ⚠ VALIDATE
            </div>
            <div style={{ fontSize:11, color:C.mid, lineHeight:1.55, flex:1 }}>
              {validationNote}
            </div>
          </div>
        </div>
      )}

      {/* ── SOURCES — dim footer ──────────────────────────── */}
      {sourcesClean && (
        <div style={{
          padding:'9px 20px 12px',
          borderTop:`1px solid ${C.b1}`,
          background:'rgba(85,85,102,.04)',
        }}>
          <div style={{
            fontFamily:F.mono, fontSize:8, letterSpacing:'0.16em', textTransform:'uppercase',
            color:C.dim, marginBottom:4,
          }}>
            ◌ Sources
          </div>
          <div style={{ fontSize:10, color:C.dim, lineHeight:1.5, fontStyle:'italic' }}>
            {sourcesClean}
          </div>
        </div>
      )}
    </div>
  );
}

// Sub-component: labeled stat cell with consistent rhythm
function StatCell({ label, children }) {
  return (
    <div>
      <div style={{
        fontFamily:F.mono, fontSize:8, letterSpacing:'0.18em', textTransform:'uppercase',
        color:C.dim, marginBottom:5, fontWeight:500,
      }}>
        {label}
      </div>
      {children}
    </div>
  );
}

// Split the persisted data_sources string back into clean sources +
// optional validation_note. The propose-intervention global_pattern
// path appends "\n\nVALIDATION: ..." — we round-trip that here.
function splitValidation(dataSources) {
  if (!dataSources) return { sourcesClean: '', validationNote: null };
  const idx = dataSources.indexOf('\n\nVALIDATION:');
  if (idx === -1) return { sourcesClean: dataSources.trim(), validationNote: null };
  return {
    sourcesClean: dataSources.slice(0, idx).trim(),
    validationNote: dataSources.slice(idx + '\n\nVALIDATION:'.length).trim(),
  };
}

// ─── Provenance footer ────────────────────────────────────
function Provenance({ run, comparisons, interventions }) {
  return (
    <div style={{
      marginTop:24, padding:'14px 18px',
      background:C.s1, border:`1px solid ${C.b1}`, borderRadius:6,
      fontSize:10, color:C.dim, lineHeight:1.6, fontFamily:F.mono,
    }}>
      <div style={{
        fontSize:8, letterSpacing:'0.18em', textTransform:'uppercase',
        color:C.gold, marginBottom:8, fontWeight:500,
      }}>
        Provenance — what these numbers actually are
      </div>
      <div style={{ marginBottom:6 }}>
        Findings derived from run <span style={{ color:C.text }}>{run?.id?.slice(0, 8)}</span> on{' '}
        {run?.completed_at ? new Date(run.completed_at).toLocaleString() : '—'} ·{' '}
        {comparisons?.length || 0} comparisons evaluated · methodology versions stored per comparison.
      </div>
      <div style={{ marginBottom:6 }}>
        Watch list = events sorted by Tipping-Point Signature composite score (lag-1 autocorrelation 50%, variance trend 30%, coefficient of variation 20%) computed on raw_data.sample (last ~10 years per event).
      </div>
      <div style={{ marginBottom:6 }}>
        Intervention costs are AI-generated rough estimates from /api/continuum/propose-intervention, NOT validated quotes. Treat as planning-stage figures requiring real-world validation before commitment. {interventions?.length || 0} interventions in library.
      </div>
      <div>
        Urgency flag fires when an intervention&apos;s target appears in the watch list (tipping score &gt;= 0.6).
      </div>
    </div>
  );
}

// ─── Section shell — consistent header/spacing ────────────
function SectionShell({ title, subtitle, children }) {
  return (
    <div style={{ marginBottom:22 }}>
      <div style={{ marginBottom:10 }}>
        <div style={{
          fontFamily:F.mono, fontSize:9, letterSpacing:'0.18em', textTransform:'uppercase',
          color:C.amber, fontWeight:500, marginBottom:3,
        }}>
          ◆ {title}
        </div>
        <div style={{ fontFamily:F.serif, fontSize:18, fontWeight:300, color:C.text, marginBottom:2 }}>
          {subtitle}
        </div>
      </div>
      {children}
    </div>
  );
}
